import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/movie";

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  const go = useCallback(
    (next: number) => {
      setIndex((next + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const active = slides[index];

  return (
    <section className="relative h-[440px] w-full overflow-hidden md:h-[600px]">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} - 鸭王在线观看`}
            className={`h-full w-full object-cover object-top ${
              i === index ? "animate-zoom-slow" : ""
            }`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/70 to-background-950/30"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>

      <div className="relative mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-12 md:px-6 md:pb-20">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-950">
              重点推荐
            </span>
            {active.genres.map((g) => (
              <span
                key={g}
                className="rounded-full border border-foreground-700/50 px-3 py-1 text-xs text-foreground-800"
              >
                {g}
              </span>
            ))}
          </div>
          <h2 className="font-heading text-3xl font-extrabold text-foreground-950 md:text-5xl">
            {active.title}
          </h2>
          <p className="mt-1 text-sm text-foreground-700 md:text-base">
            {active.year} 年上映
          </p>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {active.logline}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/detail/${active.id}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-700/60 px-6 py-3 text-sm font-medium text-foreground-900 transition-colors hover:bg-background-100/40"
            >
              <i className="ri-information-line text-lg"></i>
              影片详情
            </Link>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到 ${slide.title}`}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                i === index ? "w-8 bg-accent-500" : "w-4 bg-foreground-700/50 hover:bg-foreground-600"
              }`}
            ></button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一部"
        className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-foreground-700/40 bg-background-950/50 text-foreground-950 transition-colors hover:bg-background-950/80 md:left-6 md:flex"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一部"
        className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-foreground-700/40 bg-background-950/50 text-foreground-950 transition-colors hover:bg-background-950/80 md:right-6 md:flex"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>
    </section>
  );
}